"use client";

import Link from "next/link";
import Image from "next/image";
import { X, MessageCircle, Globe } from "lucide-react";

export function SwapperFooter() {
  const navigationLinks = {
    navigate: [
      { label: "Home", href: "/" },
      { label: "Quests", href: "/quests" },
      { label: "Referrals", href: "/referrals" },
      { label: "Leaderboard", href: "/leaderboard" },
    ],
    followUs: [
      {
        label: "X",
        href: "https://x.com/swapperfinance",
        icon: X,
      },
      {
        label: "Discord",
        href: "https://discord.com/invite/y8eevERxBz",
        icon: MessageCircle,
      },
      {
        label: "Website",
        href: "https://swapper.finance/",
        icon: Globe,
      },
    ],
  };

  return (
    <footer className="relative text-white overflow-hidden w-full h-[938px] md:h-[554px]">
      {/* Background Image */}
      <Image
        src="https://pulsarmoney.b-cdn.net/quests/campaigns/swapper-portal/footer-bg-mobile.png"
        alt="Footer Background"
        width={378}
        height={469}
        priority
        className="md:hidden absolute inset-0 w-full object-bottom"
        quality={100}
      />
      <Image
        src="https://pulsarmoney.b-cdn.net/quests/campaigns/swapper-portal/footer-bg-desktop.png"
        alt="Footer Background"
        width={1354}
        height={554}
        priority
        className="hidden md:block absolute inset-0 w-full h-full object-bottom"
        quality={100}
      />

      <div className="relative z-10 container mx-auto px-8 md:px-12 lg:px-16 py-12 md:py-16">
        {/* Mobile Layout */}
        <div className="md:hidden space-y-8">
          {/* Logo and tagline */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <Image
                src="/assets/swapper-logo-white.png"
                alt="Swapper"
                width={80}
                height={80}
                className="w-20 h-20"
              />
            </div>
            <p className="text-white/90 text-base">
              One widget. All deposits. Zero friction.
            </p>
          </div>

          {/* Powered By */}
          <div className="text-center space-y-3">
            <span className="text-white/70 text-sm block">Powered By</span>
            <div className="flex items-center justify-center gap-6">
              <Image
                src="/assets/footer/mastercard.png"
                alt="Mastercard"
                width={60}
                height={36}
                className="h-9 w-auto opacity-90"
              />
              <Image
                src="/assets/footer/chainlink.png"
                alt="Chainlink"
                width={100}
                height={36}
                className="h-9 w-auto opacity-90"
              />
            </div>
          </div>

          {/* Navigation and Follow Us - Two Columns */}
          <div className="grid grid-cols-2 gap-8">
            {/* Navigation */}
            <div className="text-center">
              <h3 className="text-white font-semibold text-lg mb-4">Navigate</h3>
              <ul className="space-y-3">
                {navigationLinks.navigate.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-white/80 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Follow Us */}
            <div className="text-center">
              <h3 className="text-white font-semibold text-lg mb-4">Follow Us</h3>
              <ul className="space-y-3">
                {navigationLinks.followUs.map((link) => {
                  const IconComponent = link.icon;
                  return (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-200 text-sm"
                      >
                        <IconComponent className="w-4 h-4" />
                        {link.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid md:grid-cols-[2fr_auto_auto] gap-12 lg:gap-16 xl:gap-20">
          {/* Left Column - Logo and Powered By */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Image
                src="/assets/swapper-logo-white.png"
                alt="Swapper"
                width={64}
                height={64}
                className="w-16 h-16"
              />
            </div>

            <p className="text-white/90 text-base max-w-xs">
              One widget. All deposits. Zero friction.
            </p>

            <div className="space-y-3">
              <span className="text-white/70 text-sm block">Powered By</span>
              <div className="flex items-center gap-6">
                <Image
                  src="/assets/footer/mastercard.png"
                  alt="Mastercard"
                  width={60}
                  height={36}
                  className="h-9 w-auto opacity-90"
                />
                <Image
                  src="/assets/footer/chainlink.png"
                  alt="Chainlink"
                  width={100}
                  height={36}
                  className="h-9 w-auto opacity-90"
                />
              </div>
            </div>
          </div>

          {/* Middle Column - Navigate */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Navigate</h3>
            <ul className="space-y-3">
              {navigationLinks.navigate.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-white/80 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column - Follow Us */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Follow Us</h3>
            <ul className="space-y-3">
              {navigationLinks.followUs.map((link) => {
                const IconComponent = link.icon;
                return (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-200"
                    >
                      <IconComponent className="w-4 h-4" />
                      {link.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
